import { isSameDay, parseISO } from "date-fns";
import { ArrowUpRight, Coffee } from "lucide-react";
import { catStyle } from "@/lib/constants";
import { fmtTime } from "@/lib/time";

function findFocus(tasks, events) {
  const now = new Date();
  const blocks = tasks
    .filter((t) => t.status === "todo")
    .flatMap((t) => t.blocks.map((b) => ({ task: t, start: parseISO(b.start), end: parseISO(b.end), b })))
    .filter((x) => isSameDay(x.start, now) && x.end > now)
    .sort((a, b) => a.start - b.start);
  const meeting = events.find((e) => parseISO(e.start) <= now && parseISO(e.end) > now);
  const current = blocks.find((x) => x.start <= now);
  return { meeting, current, next: blocks.find((x) => x.start > now), left: blocks.length };
}

export default function FocusCard({ tasks, events, onOpen }) {
  const { meeting, current, next, left } = findFocus(tasks, events);
  const item = current || next;
  const c = item ? catStyle(item.task.category) : null;
  let label = "Nothing else planned for today";
  if (meeting) label = `In "${meeting.title}" until ${fmtTime(meeting.end)}`;
  else if (current) label = `Do this now · until ${fmtTime(current.b.end)}`;
  else if (next) label = `Up next at ${fmtTime(next.b.start)}`;
  return (
    <section className="relative overflow-hidden rounded-2xl bg-[#103080] text-white p-6 sm:p-8 ambient fade-up" data-testid="focus-card">
      <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#0090D0]/30 blur-3xl" />
      <img src="/logo-mark.png" alt="" className="absolute right-6 bottom-4 w-24 opacity-10 brightness-0 invert" />
      <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.3em] text-[#40C0E0] font-semibold" data-testid="focus-label">{label}</p>
          {item ? (
            <>
              <h2 className="font-serif-brand font-semibold text-2xl sm:text-3xl mt-3 truncate" data-testid="focus-task-title">{item.task.title}</h2>
              <p className="text-white/70 text-sm mt-2">
                <span className="inline-block rounded-full px-2.5 py-0.5 mr-2 text-xs font-semibold" style={{ background: c.bg, color: c.text }}>{item.task.category}</span>
                {item.task.client_name} · {fmtTime(item.b.start)}–{fmtTime(item.b.end)}
              </p>
            </>
          ) : (
            <h2 className="font-serif-brand font-semibold text-2xl sm:text-3xl mt-3 inline-flex items-center gap-3">
              <Coffee size={26} className="text-[#40C0E0]" /> You're <i className="text-[#40C0E0]">all clear.</i>
            </h2>
          )}
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right">
            <p className="font-serif-brand text-3xl font-semibold" data-testid="focus-blocks-left">{left}</p>
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/60">blocks left today</p>
          </div>
          {item && (
            <button onClick={() => onOpen(item.task)} data-testid="focus-open-task-button" aria-label="Open task"
              className="w-12 h-12 rounded-full bg-white text-[#103080] flex items-center justify-center hover:bg-[#40C0E0] transition-colors duration-200">
              <ArrowUpRight size={20} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
